import type { RosterPermit, TenantInvoiceRecord, TenantInvoiceStatus, Validation } from './api'

const money = (cents: number) =>
  `$${(Math.max(0, cents) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const calendarDate = (iso: string): string => {
  const [year, month, day] = iso.slice(0, 10).split('-').map(Number)
  if (!year || !month || !day) return iso
  return new Date(year, month - 1, day).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

/**
 * Today's LOCAL calendar day as "YYYY-MM-DD" — for a permit end-date input's
 * min bound and "is this date already past" checks against `validUntil`
 * (a plain calendar date with no time or zone attached, same as
 * ops/src/utils/datetime.ts's toCalendarDate).
 *
 * NOT `new Date().toISOString().slice(0, 10)`: that converts to UTC first, so
 * any evening in a zone west of UTC (all of Parkify's) rolls to tomorrow — a
 * manager approving or adding a parker after ~8pm ET could not set an end
 * date of "today" (the DatePicker's min was already tomorrow, and the
 * validUntil >= today check failed for the date they just picked).
 */
export function todayCalendarDate(d: Date = new Date()): string {
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${mm}-${dd}`
}

/** A permit's end state, stated next to its status instead of hidden in Edit. */
export function permitEndLabel(status: RosterPermit['status'], validUntil: string): string {
  if (!validUntil) return 'No scheduled end'
  return `${status === 'expired' ? 'Ended' : 'Ends'} ${calendarDate(validUntil)}`
}

/** The day a permit starts, shown with its end so a prorated first bill can be checked. */
export function permitStartLabel(validFrom: string | undefined): string | null {
  return validFrom ? `Starts ${calendarDate(validFrom)}` : null
}

export function offerFundingLine(o: Validation): { label: string; detail: string } {
  if (o.funding === 'included') return { label: 'Included', detail: 'No cost to you · no program limit' }
  if (o.funding === 'chargeback') {
    if (o.spendLimit?.kind === 'amount') return { label: 'Billed per use', detail: `${money(o.spendLimit.cents)} monthly limit` }
    if (o.spendLimit?.kind === 'uncapped') return { label: 'Billed per use', detail: 'No monthly limit' }
    return { label: 'Billed per use', detail: 'Monthly limit not set' }
  }
  if (o.funding === 'prepaid') {
    return {
      label: 'Prepaid',
      detail: o.prepaidLimit == null ? 'Block size not set' : `${o.prepaidLimit.toLocaleString('en-US')} validation block`,
    }
  }
  return { label: 'Funding not set', detail: 'Ask your Parkify contact' }
}

export function statementStatusSummary(invoices: TenantInvoiceRecord[]): string {
  if (invoices.length === 0) return 'No earlier statements'
  const count = (status: TenantInvoiceStatus) => invoices.filter(i => i.status === status).length
  return [
    count('issued') ? `${count('issued')} issued` : '',
    count('draft') ? `${count('draft')} draft` : '',
    count('paid') ? `${count('paid')} paid` : '',
    count('void') ? `${count('void')} void` : '',
  ].filter(Boolean).join(' · ')
}
